/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, Table, Button, Form, Badge, Alert,
} from 'react-bootstrap';
import OperatorService from '../../../Services/OperatorService';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import { driverNameErrors, hasDriverName } from '../../../Utils/driverName';
import { groupFor, operatorId as recordId } from '../operatorGroups';
import {
  activeSiblings,
  driverId as rowId,
  personEntries,
  personIdOf,
  planPersonLink,
  planPersonUnlink,
} from '../driverPeople';

/**
 * The collection, for a caller that has none to give.
 *
 * A module constant and NOT an inline `= []` default, which is the whole
 * point of it. A default written in the parameter list builds a fresh array
 * on every render, so its identity changes every time — and the fetch effect
 * below watches the collection. Defaulted inline, every render re-ran the
 * effect, every fetch re-rendered, and the modal never settled: it rendered
 * nothing a test could wait for and hammered the drivers endpoint in a
 * browser. One shared frozen array is one identity, for ever.
 */
const NO_OPERATORS = Object.freeze([]);

/**
 * The office's driver screen, for a COMPANY rather than for one record.
 *
 * A company that settles in two currencies is two operator records
 * (operatorGroups), and a driver row belongs to exactly one of them — so the
 * same human hired by such a company holds two rows. This screen lists every
 * record of the company, says which record each row sits on, and collapses
 * the rows the office has linked (`person_id`) into ONE entry per human, by
 * the same rule the operator's own portal roster runs on (driverPeople).
 *
 * A record standing on its own is unaffected: one fetch, one row per driver,
 * no record column, exactly as before.
 */
function DriverManagementModal({
  operator, operators = NO_OPERATORS, show = false, handleClose,
}) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [linkSource, setLinkSource] = useState(null);
  const [linkTargetId, setLinkTargetId] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    active: 1,
  });

  // The company behind the open record, and every record it answers for.
  // Without the whole collection this is a group of one, which is exactly
  // the screen as it was before person links existed.
  const group = useMemo(() => groupFor(operator, operators), [operator, operators]);
  const memberIds = useMemo(() => group.members.map(recordId), [group]);
  const multiRecord = group.members.length > 1;

  const recordName = (id) => {
    const member = group.members.find((row) => recordId(row) === String(id));

    return member?.name || String(id || '');
  };

  const fetchDrivers = async () => {
    if (!operator?.Id) return;

    setLoading(true);
    try {
      const responses = await Promise.all(memberIds.map((id) => OperatorService.fetchDrivers(id)));

      setDrivers(responses.flatMap((response, index) => (
        (response?.data?.data || []).map((driver) => ({
          ...driver,
          // Stamped from the record it was fetched under when the row does
          // not carry it, so "which record is this on" is always answerable
          // — and so a save can never post the wrong record back
          // (see driverPeople).
          Operator_Id: driver.Operator_Id || memberIds[index],
        }))
      )));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  // The records this screen asks, as one value it can be compared by. The
  // collection arrives from a page that re-creates it, so keying the fetch on
  // the ARRAY re-fetches every roster whenever that page happens to
  // re-render; keying it on the ids re-fetches only when the ids differ.
  const memberKey = memberIds.join('|');

  useEffect(() => {
    if (show && operator?.Id) {
      fetchDrivers();
    }
    // `fetchDrivers` reads `memberIds`, which `memberKey` changes with, so
    // the closure is never stale. (This repo's eslint carries no react-hooks
    // plugin, so there is no exhaustive-deps to silence here.)
  }, [show, operator?.Id, memberKey]);

  // One entry per human. The company's own records are the preference order
  // for which row represents a person, primary first.
  const entries = useMemo(() => personEntries(drivers, memberIds), [drivers, memberIds]);

  const handleChange = (e) => {
    const {
      id, value, type, checked,
    } = e.target;
    // Remove 'add_' prefix from controlId to get actual field name
    const fieldName = id.replace('add_', '');
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone_number: '',
      active: 1,
    });
    setValidationErrors([]);
    setError('');
  };

  const handleAddDriver = async () => {
    // Said here as well as by the API, because the API's answer costs a
    // round trip and Booking.com's costs a trip with no driver on it.
    const missing = driverNameErrors(formData);

    if (missing.length) {
      setValidationErrors(missing);
      return;
    }

    setLoading(true);
    try {
      // No `person_id`: a brand-new row is nobody yet, and ABSENT is the
      // only value that means "leave the link alone".
      await OperatorService.addDriver({
        ...formData,
        operator_id: operator.Id,
        active: Number(formData.active),
      });
      await fetchDrivers();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add driver');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDriver = async (driver) => {
    const missing = driverNameErrors(driver);

    if (missing.length) {
      setValidationErrors(missing);
      return;
    }

    setLoading(true);
    try {
      await OperatorService.addDriver({
        Id: driver.Id,
        first_name: driver.first_name,
        last_name: driver.last_name,
        phone_number: driver.phone_number,
        // The row's OWN record. This screen lists both records of a company,
        // so posting the open one would re-home a sibling row to the other
        // currency bucket as a side effect of fixing a phone number.
        operator_id: driver.Operator_Id || operator.Id,
        active: Number(driver.active),
        // `person_id` is deliberately NOT here. The API leaves the link
        // alone only while the field is ABSENT — sending it as '' or
        // undefined would unlink the row, so every ordinary edit would
        // quietly undo a link the office made on purpose.
      });
      await fetchDrivers();
      setEditingDriverId(null);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update driver');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const runLinkWrites = async (writes) => {
    setLoading(true);
    try {
      // Sequential: two writes to the same person, and the second is only
      // worth sending if the first was accepted.
      // eslint-disable-next-line no-restricted-syntax
      for (const body of writes) {
        // eslint-disable-next-line no-await-in-loop
        await OperatorService.addDriver(body);
      }
      await fetchDrivers();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the person link');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const closeLink = () => {
    setLinkSource(null);
    setLinkTargetId('');
  };

  const handleConfirmLink = async () => {
    const target = drivers.find((row) => rowId(row) === String(linkTargetId));
    const plan = planPersonLink(linkSource, target);

    if (plan.blocked) {
      setError(plan.blocked);
      return;
    }

    if (await runLinkWrites(plan.writes)) {
      closeLink();
    }
  };

  const handleUnlink = async (driver) => {
    const plan = planPersonUnlink(driver);

    if (plan.blocked) {
      setError(plan.blocked);
      return;
    }

    await runLinkWrites(plan.writes);
  };

  const handleDeleteDriver = async (driver) => {
    setRemoveTarget(null);
    setLoading(true);
    try {
      await OperatorService.deleteDriver(rowId(driver));
      await fetchDrivers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete driver');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (driver) => {
    setEditingDriverId(driver.Id);
  };

  const cancelEditing = () => {
    setEditingDriverId(null);
    fetchDrivers();
  };

  const handleEditChange = (driverId, field, value) => {
    setDrivers((prevDrivers) => prevDrivers.map((d) => (
      d.Id === driverId ? { ...d, [field]: value } : d
    )));
  };

  const toggleExpanded = (key) => setExpanded((prev) => (
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  ));

  // What the link panel would do, previewed without minting anything: only
  // the refusal and whether there is work to do are shown here.
  const linkPreview = linkSource
    ? planPersonLink(linkSource, drivers.find((row) => rowId(row) === String(linkTargetId)), () => 'preview')
    : null;

  const linkCandidates = linkSource
    ? drivers.filter((row) => rowId(row) !== rowId(linkSource))
    : [];

  const removeSiblings = removeTarget ? activeSiblings(removeTarget, drivers) : [];

  const columnCount = multiRecord ? 5 : 4;

  const driverActions = (driver) => (
    <div className="d-flex gap-1">
      <Button
        variant="primary"
        size="sm"
        title="Edit"
        aria-label={`Edit ${driver.full_name}`}
        onClick={() => startEditing(driver)}
        disabled={loading}
      >
        <i className="fa fa-edit" />
      </Button>
      {personIdOf(driver) ? (
        <Button
          variant="outline-secondary"
          size="sm"
          title="Unlink"
          aria-label={`Unlink ${driver.full_name}`}
          onClick={() => handleUnlink(driver)}
          disabled={loading}
        >
          <i className="fa fa-chain-broken" />
        </Button>
      ) : (
        <Button
          variant="outline-secondary"
          size="sm"
          title="Same person as…"
          aria-label={`Link ${driver.full_name} to another driver`}
          onClick={() => { setLinkSource(driver); setLinkTargetId(''); setError(''); }}
          disabled={loading || drivers.length < 2}
        >
          <i className="fa fa-link" />
        </Button>
      )}
      {driver.active === 1 && (
        <Button
          variant="danger"
          size="sm"
          title="Remove"
          aria-label={`Remove ${driver.full_name}`}
          onClick={() => setRemoveTarget(driver)}
          disabled={loading}
        >
          <i className="fa fa-trash" />
        </Button>
      )}
    </div>
  );

  const activeBadge = (driver) => (driver.active === 1 ? (
    <Badge bg="success">Active</Badge>
  ) : (
    <Badge bg="danger">Inactive</Badge>
  ));

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Driver Management -
          {' '}
          {multiRecord ? group.name : operator?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        {multiRecord && (
          <Alert variant="light" className="border small py-2">
            <i className="fa fa-building me-2" />
            {group.name}
            {' keeps '}
            {group.members.length}
            {' operator records — '}
            {group.members.map((member) => member.name).join(' and ')}
            {'. Every record\'s drivers are listed here, and rows marked as the same person are shown once.'}
          </Alert>
        )}

        {/* Linking is "the same human as THAT row". There is no list of
            people to pick from — `person_id` is an opaque key with no table
            behind it, borrowed from the row pointed at or minted when
            neither row carries one. */}
        {linkSource && (
          <div className="card mb-3 border-secondary">
            <div className="card-body">
              <h6 className="card-title">
                {'Who is '}
                {linkSource.full_name}
                ?
              </h6>
              <p className="small text-muted mb-2">
                Pick the driver row that is the same human. Nothing is guessed from names or
                phone numbers — this is only recorded because you say so.
              </p>
              <Form.Group className="mb-2" controlId="link_target">
                <Form.Label>Same person as</Form.Label>
                <Form.Select
                  aria-label="Same person as"
                  value={linkTargetId}
                  onChange={(e) => setLinkTargetId(e.target.value)}
                  size="sm"
                >
                  <option value="">Choose a driver…</option>
                  {linkCandidates.map((row) => (
                    <option key={rowId(row)} value={rowId(row)}>
                      {row.full_name}
                      {multiRecord ? ` — ${recordName(row.Operator_Id)}` : ''}
                      {Number(row.active) === 1 ? '' : ' (inactive)'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {linkTargetId && linkPreview?.blocked && (
                <div className="text-danger small mb-2">{linkPreview.blocked}</div>
              )}

              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleConfirmLink}
                  disabled={loading || !linkTargetId || Boolean(linkPreview?.blocked)}
                >
                  <i className="fa fa-check me-2" />
                  Same person
                </Button>
                <Button variant="secondary" size="sm" onClick={closeLink} disabled={loading}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* The office's Remove switches off the ONE row it names. It does
            NOT cascade to the person's other rows — the operator portal's
            own Remove does, this one does not — so a linked driver can be
            "removed" here and still be driving, and still logging in,
            through a sibling row. */}
        {removeTarget && (
          <Alert variant={removeSiblings.length ? 'warning' : 'light'} className="border">
            <div className="fw-bold mb-1">
              {'Remove '}
              {removeTarget.full_name}
              {multiRecord ? ` from ${recordName(removeTarget.Operator_Id)}` : ''}
              ?
            </div>
            {removeSiblings.length > 0 ? (
              <>
                <p className="mb-2">
                  This only switches off this one row. The same person is still active on
                  {' '}
                  {removeSiblings.length === 1 ? 'another row' : `${removeSiblings.length} other rows`}
                  , so they keep their portal login and go on seeing their work:
                </p>
                <ul className="mb-2">
                  {removeSiblings.map((row) => (
                    <li key={rowId(row)}>
                      {row.full_name}
                      {' — '}
                      {recordName(row.Operator_Id)}
                    </li>
                  ))}
                </ul>
                <p className="mb-2 small">
                  To stop this person driving for the company altogether, remove every row
                  listed above as well.
                </p>
              </>
            ) : (
              <p className="mb-2">This deactivates the driver. It can be undone by editing them back to Active.</p>
            )}
            <div className="d-flex gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDeleteDriver(removeTarget)}
                disabled={loading}
              >
                Remove this row
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setRemoveTarget(null)} disabled={loading}>
                Cancel
              </Button>
            </div>
          </Alert>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Drivers</h6>
          {!showAddForm && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowAddForm(true)}
              disabled={loading}
            >
              <i className="fa fa-plus me-2" />
              Add New Driver
            </Button>
          )}
        </div>

        {showAddForm && (
          <div className="card mb-3 bg-light">
            <div className="card-body">
              <h6 className="card-title">
                Add New Driver
                {multiRecord ? ` to ${operator?.name}` : ''}
              </h6>
              <Form>
                <div className="row">
                  <Form.Group className="mb-2 col-md-3" controlId="add_first_name">
                    <Form.Label>First Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Péter"
                      value={formData.first_name}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="first_name" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_last_name">
                    <Form.Label>Last Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Kovács"
                      value={formData.last_name}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="last_name" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_phone_number">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.phone_number}
                      onChange={handleChange}
                      size="sm"
                    />
                    <FormControlFeedback errors={validationErrors} field="phone_number" />
                  </Form.Group>

                  <Form.Group className="mb-2 col-md-3" controlId="add_active">
                    <Form.Label>Active *</Form.Label>
                    <Form.Select
                      value={formData.active}
                      onChange={handleChange}
                      size="sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddDriver}
                    disabled={loading || !hasDriverName(formData)}
                  >
                    <i className="fa fa-check me-2" />
                    Save
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        )}

        <Table striped bordered hover size="sm" responsive className="fleet-driver-table">
          <thead>
            <tr>
              <th>Full Name</th>
              {multiRecord && <th>Record</th>}
              <th>Phone Number</th>
              <th>Active</th>
              <th style={{ width: '150px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="text-center text-muted">
                  No drivers found
                </td>
              </tr>
            )}
            {entries.map((entry) => {
              const driver = entry.representative;
              const isOpen = expanded.includes(entry.key);

              return (
                <React.Fragment key={entry.key}>
                  <tr
                    className={driver.active === 0 ? 'text-muted' : ''}
                    style={{ opacity: driver.active === 0 ? 0.6 : 1 }}
                  >
                    {editingDriverId === driver.Id ? (
                      <>
                        {/* One column, two boxes: the list shows the composed name,
                            but what is EDITED is the two halves the partner gets. */}
                        <td data-label="Name">
                          <div className="d-flex gap-1">
                            <Form.Control
                              type="text"
                              aria-label={`First name of ${driver.full_name}`}
                              placeholder="First name"
                              value={driver.first_name || ''}
                              onChange={(e) => handleEditChange(driver.Id, 'first_name', e.target.value)}
                              size="sm"
                            />
                            <Form.Control
                              type="text"
                              aria-label={`Last name of ${driver.full_name}`}
                              placeholder="Last name"
                              value={driver.last_name || ''}
                              onChange={(e) => handleEditChange(driver.Id, 'last_name', e.target.value)}
                              size="sm"
                            />
                          </div>
                          <FormControlFeedback errors={validationErrors} field="first_name" />
                          <FormControlFeedback errors={validationErrors} field="last_name" />
                        </td>
                        {multiRecord && <td data-label="Record">{recordName(driver.Operator_Id)}</td>}
                        <td data-label="Phone">
                          <Form.Control
                            type="text"
                            value={driver.phone_number || ''}
                            onChange={(e) => handleEditChange(driver.Id, 'phone_number', e.target.value)}
                            size="sm"
                          />
                        </td>
                        <td data-label="Active">
                          <Form.Select
                            value={driver.active}
                            onChange={(e) => handleEditChange(driver.Id, 'active', Number(e.target.value))}
                            size="sm"
                          >
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                          </Form.Select>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleUpdateDriver(driver)}
                              disabled={loading || !hasDriverName(driver)}
                            >
                              <i className="fa fa-check" />
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={cancelEditing}
                              disabled={loading}
                            >
                              <i className="fa fa-times" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td data-label="Name">
                          {driver.full_name}
                          {hasDriverName(driver) ? null : (
                            <div className="text-danger small">Needs a first and a last name</div>
                          )}
                          {entry.shared && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 d-block small text-decoration-none"
                              aria-expanded={isOpen}
                              onClick={() => toggleExpanded(entry.key)}
                            >
                              <i className={`fa fa-chevron-${isOpen ? 'down' : 'right'} me-1`} />
                              {`Same person on ${entry.rows.length} rows`}
                            </Button>
                          )}
                        </td>
                        {multiRecord && (
                          <td data-label="Record">
                            {entry.shared
                              ? [...new Set(entry.rows.map((row) => recordName(row.Operator_Id)))].join(', ')
                              : recordName(driver.Operator_Id)}
                          </td>
                        )}
                        <td data-label="Phone">{driver.phone_number || '-'}</td>
                        <td data-label="Active">{activeBadge(driver)}</td>
                        <td>{driverActions(driver)}</td>
                      </>
                    )}
                  </tr>

                  {/* The rows behind one person, each on its own record and
                      each removable on its own — the office acts on rows,
                      even though it reads a person. */}
                  {entry.shared && isOpen && entry.rows.map((row) => (
                    <tr key={rowId(row)} className="small bg-light">
                      <td data-label="Name" className="ps-4">
                        <i className="fa fa-user me-2 text-muted" />
                        {row.full_name}
                      </td>
                      {multiRecord && <td data-label="Record">{recordName(row.Operator_Id)}</td>}
                      <td data-label="Phone">{row.phone_number || '-'}</td>
                      <td data-label="Active">{activeBadge(row)}</td>
                      <td>{driverActions(row)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DriverManagementModal;
